/**
 * Search/social metadata for the static site, as pure string functions (no fs, no Vite) so
 * `node --test` covers them. Used at build time by the plugins in src/blog/vitePlugin.ts:
 *   - injectSocialMeta: fills in the Open Graph / Twitter tags every indexable page needs,
 *     from its own <title>, description and canonical. A page that already sets a tag keeps
 *     its value; pages without a canonical (404, /tour, /app) are left alone.
 *   - homeJsonLd: Organization + MobileApplication + WebSite for each language's home.
 *   - pagePath / langMenu: where each site page lives per language, and the header's
 *     language menu.
 *   - sitemapXml / robotsTxt.
 */
import { SITE } from './config.js';

/** The site's languages, the same six as the app. Spanish lives at the root (/, /blog…),
 *  English under /en, the rest under /fr, /de, /it, /pt with English path segments. */
export const LANGS = ['es', 'en', 'fr', 'de', 'it', 'pt'] as const;
export type Lang = (typeof LANGS)[number];

export function isLang(v: unknown): v is Lang {
  return (LANGS as readonly unknown[]).includes(v);
}

/** Each language named in itself (menus, titles). */
export const LANG_NAMES: Record<Lang, string> = {
  es: 'Español',
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
};

/** The hand-written site pages (static HTML or generated singletons) and their paths. */
export type SitePage = 'home' | 'features' | 'pricing' | 'compare' | 'blog' | 'about' | 'support' | 'plan3d' | 'privacy' | 'terms';

const ES_EN_PATHS: Record<SitePage, readonly [es: string, en: string]> = {
  home: ['/', '/en'],
  features: ['/funciones', '/en/features'],
  pricing: ['/precios', '/en/pricing'],
  compare: ['/comparar', '/en/compare'],
  blog: ['/blog', '/en/blog'],
  about: ['/sobre', '/en/about'],
  support: ['/soporte', '/en/support'],
  plan3d: ['/de-plano-a-3d', '/en/plan-to-3d'],
  privacy: ['/privacidad', '/en/privacy'],
  terms: ['/terminos', '/en/terms'],
};

/** Legal pages exist in Spanish and English only; the other languages link the English. */
const ES_EN_ONLY: ReadonlySet<SitePage> = new Set(['privacy', 'terms']);

export function pagePath(page: SitePage, lang: Lang): string {
  const [es, en] = ES_EN_PATHS[page];
  if (lang === 'es') return es;
  if (lang === 'en' || ES_EN_ONLY.has(page)) return en;
  return `/${lang}${en.slice('/en'.length)}`;
}

/** The languages a site page really exists in. */
export function pageLangs(page: SitePage): readonly Lang[] {
  return ES_EN_ONLY.has(page) ? ['es', 'en'] : LANGS;
}

export const SITE_PAGES = Object.keys(ES_EN_PATHS) as SitePage[];

const MENU_LABEL: Record<Lang, string> = { es: 'Idioma', en: 'Language', fr: 'Langue', de: 'Sprache', it: 'Lingua', pt: 'Idioma' };

/**
 * The header's language menu: a <details> (works without JS) whose summary shows the current
 * language code. `hrefs` maps each language to this page's version in it, or wherever that
 * language should land when the page has no version (its home or index).
 */
export function langMenu(current: Lang, hrefs: Record<Lang, string>): string {
  const items = LANGS.map(
    (l) =>
      `<li><a href="${escapeHtml(hrefs[l])}" hreflang="${l}" lang="${l}"${l === current ? ' aria-current="page"' : ''}>${LANG_NAMES[l]}</a></li>`,
  ).join('');
  return `<details class="lang-menu"><summary aria-label="${MENU_LABEL[current]}: ${LANG_NAMES[current]}" title="${MENU_LABEL[current]}">${current.toUpperCase()}</summary><ul>${items}</ul></details>`;
}

/** pt is Brazilian Portuguese, like the app's catalog. */
const OG_LOCALE: Record<Lang, string> = { es: 'es_ES', en: 'en_GB', fr: 'fr_FR', de: 'de_DE', it: 'it_IT', pt: 'pt_BR' };

const OG_IMAGE_ALT: Record<Lang, string> = {
  es: 'Salón luminoso en un tour virtual',
  en: 'A bright living room in a virtual tour',
  fr: 'Un salon lumineux dans une visite virtuelle',
  de: 'Ein helles Wohnzimmer in einem virtuellen Rundgang',
  it: 'Un soggiorno luminoso in un tour virtuale',
  pt: 'Uma sala iluminada em um tour virtual',
};

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A `<script type="application/ld+json">` block; `<` is escaped so content can't close it. */
export function jsonLdScript(data: unknown): string {
  const json = JSON.stringify(data).replace(/</g, '\\u003c');
  return `<script type="application/ld+json">${json}</script>`;
}

export function absoluteUrl(path: string, origin: string = SITE.ORIGIN): string {
  return /^https?:\/\//.test(path) ? path : `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

function attr(html: string, re: RegExp): string | undefined {
  const m = html.match(re);
  return m?.[1];
}

function hasMeta(html: string, key: string): boolean {
  return new RegExp(`<meta\\s+(?:property|name)="${key.replace(/[.:]/g, '\\$&')}"`, 'i').test(html);
}

/** Decode the few entities our own pages use, so a tag value isn't double-escaped. */
function decode(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/**
 * Adds the og:* and twitter:* tags a page is missing, derived from the page itself.
 * Only touches pages with `<link rel="canonical">` (= indexable).
 */
/** Favicon + home-screen icon (the app icon, see public/brand) for every page that has none. */
export function injectIcons(html: string): string {
  if (/<link\s+rel="icon"/i.test(html)) return html;
  const links =
    '<link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />\n' +
    '    <link rel="apple-touch-icon" href="/apple-touch-icon.png" />';
  return html.replace(/\s*<\/head>/i, `\n    ${links}\n  </head>`);
}

export function injectSocialMeta(html: string, origin: string = SITE.ORIGIN): string {
  const canonical = attr(html, /<link\s+rel="canonical"\s+href="([^"]+)"/i);
  if (!canonical) return html;
  const htmlLang = attr(html, /<html\s+lang="([a-z]{2})/i);
  const lang: Lang = isLang(htmlLang) ? htmlLang : 'es';
  const title = decode(attr(html, /<title>([^<]*)<\/title>/i) ?? SITE.BRAND);
  const description = decode(attr(html, /<meta\s+name="description"\s+content="([^"]*)"/i) ?? '');
  const ogTitle = decode(attr(html, /<meta\s+property="og:title"\s+content="([^"]*)"/i) ?? title);
  const ogDesc = decode(
    attr(html, /<meta\s+property="og:description"\s+content="([^"]*)"/i) ?? description,
  );
  const image = absoluteUrl(SITE.OG_IMAGE, origin);

  const wanted: [kind: 'property' | 'name', key: string, value: string][] = [
    ['property', 'og:type', 'website'],
    ['property', 'og:site_name', SITE.BRAND],
    ['property', 'og:locale', OG_LOCALE[lang]],
    ['property', 'og:title', ogTitle],
    ['property', 'og:description', ogDesc],
    ['property', 'og:url', canonical],
    ['property', 'og:image', image],
    ['property', 'og:image:width', '1200'],
    ['property', 'og:image:height', '630'],
    ['property', 'og:image:alt', OG_IMAGE_ALT[lang]],
    ['name', 'twitter:card', 'summary_large_image'],
    ['name', 'twitter:title', ogTitle],
    ['name', 'twitter:description', ogDesc],
    ['name', 'twitter:image', image],
  ];
  const tags = wanted
    .filter(([, key]) => !hasMeta(html, key))
    .filter(([, key, value]) => value !== '' || key === 'og:type')
    .map(([kind, key, value]) => `    <meta ${kind}="${key}" content="${escapeHtml(value)}" />`);
  if (tags.length === 0) return html;
  return html.replace(/\s*<\/head>/i, `\n${tags.join('\n')}\n  </head>`);
}

const ORG_ID = `${SITE.ORIGIN}/#organization`;

export function organizationLd(): Record<string, unknown> {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE.BRAND,
    url: `${SITE.ORIGIN}/`,
    email: SITE.EMAIL,
    logo: `${SITE.ORIGIN}/brand/icon-512.png`,
    // Ties the brand to its App Store listing: "roomtour" also means YouTube room-tour videos.
    sameAs: [SITE.APP_STORE_URL],
  };
}

const APP_LD: Record<Lang, { description: string; offer: string }> = {
  es: {
    description: 'App para iPhone que crea tours virtuales 360° de cualquier inmueble, sin cámara 360, y un plano aproximado en iPhone con LiDAR.',
    offer: 'Plan gratis: 1 tour publicado; todo gratis durante la oferta de lanzamiento',
  },
  en: {
    description: 'iPhone app that makes 360° virtual tours of any property without a 360 camera, plus an approximate floor plan on iPhones with LiDAR.',
    offer: 'Free plan: 1 live tour; everything free during the launch offer',
  },
  fr: {
    description: "App iPhone qui crée des visites virtuelles 360° de n'importe quel bien, sans caméra 360, et un plan approximatif sur les iPhone avec LiDAR.",
    offer: 'Offre gratuite : 1 visite en ligne ; tout est gratuit pendant l’offre de lancement',
  },
  de: {
    description: 'iPhone-App, die virtuelle 360°-Rundgänge jeder Immobilie ohne 360°-Kamera erstellt, plus einen ungefähren Grundriss auf iPhones mit LiDAR.',
    offer: 'Gratis-Tarif: 1 veröffentlichter Rundgang; während des Startangebots ist alles kostenlos',
  },
  it: {
    description: "App per iPhone che crea tour virtuali 360° di qualsiasi immobile, senza fotocamera 360, e una planimetria approssimativa sugli iPhone con LiDAR.",
    offer: "Piano gratuito: 1 tour pubblicato; tutto gratis durante l'offerta di lancio",
  },
  pt: {
    description: 'App para iPhone que cria tours virtuais 360° de qualquer imóvel, sem câmera 360, e uma planta aproximada em iPhones com LiDAR.',
    offer: 'Plano grátis: 1 tour publicado; tudo grátis durante a oferta de lançamento',
  },
};

/** JSON-LD for the home pages. Freemium → one 0 EUR offer (the free plan; Pro prices aren't public yet). */
export function homeJsonLd(lang: Lang, appStoreUrl: string = SITE.APP_STORE_URL): string {
  const home = absoluteUrl(pagePath('home', lang));
  const app: Record<string, unknown> = {
    '@type': 'MobileApplication',
    name: SITE.BRAND,
    url: home,
    description: APP_LD[lang].description,
    operatingSystem: 'iOS',
    applicationCategory: 'BusinessApplication',
    inLanguage: [...LANGS],
    image: absoluteUrl(SITE.OG_IMAGE),
    publisher: { '@id': ORG_ID },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: APP_LD[lang].offer,
    },
  };
  if (appStoreUrl) {
    app.downloadUrl = appStoreUrl;
    app.installUrl = appStoreUrl;
  }
  return jsonLdScript({
    '@context': 'https://schema.org',
    '@graph': [
      organizationLd(),
      {
        '@type': 'WebSite',
        '@id': `${SITE.ORIGIN}/#website`,
        url: `${SITE.ORIGIN}/`,
        name: SITE.BRAND,
        inLanguage: [...LANGS],
        publisher: { '@id': ORG_ID },
      },
      app,
    ],
  });
}

export interface SitemapEntry {
  /** Absolute URL. */
  loc: string;
  /** YYYY-MM-DD */
  lastmod?: string;
  /** hreflang → absolute URL, including the entry's own language. */
  alternates?: Partial<Record<Lang | 'x-default', string>>;
}

/** hreflang alternates for a page that has a twin: es, en and x-default → English (site rule). */
export function twinAlternates(esUrl: string, enUrl: string): SitemapEntry['alternates'] {
  return { es: esUrl, en: enUrl, 'x-default': enUrl };
}

/** hreflang alternates for a page translated into several languages (lang → absolute URL),
 *  or undefined when only one language exists. x-default → English, else Spanish. */
export function groupAlternates(urls: Partial<Record<Lang, string>>): SitemapEntry['alternates'] {
  if (Object.keys(urls).length < 2) return undefined;
  return { ...urls, 'x-default': urls.en ?? urls.es };
}

/** Every known page path → its version per language (a route group), for localizeLinks. */
export type RouteMap = ReadonlyMap<string, Partial<Record<Lang, string>>>;

/**
 * Points a page's internal links at its own language: an `<a href="/…">` whose path is in
 * `routes` and has a `lang` version is rewritten to it (query and hash kept). Translations
 * keep their source's links (/en/blog/x) and the build swaps them, so a translator never
 * needs to know another page's slug.
 */
export function localizeLinks(html: string, lang: Lang, routes: RouteMap): string {
  // Only <a> tags, and never one that names its own language (the language menu).
  return html.replace(/<a\s[^>]*>/g, (tag) =>
    /\shreflang="/.test(tag)
      ? tag
      : tag.replace(/href="(\/[^"#?]*)([#?][^"]*)?"/, (match, path: string, rest: string | undefined) => {
          const key = path.length > 1 ? path.replace(/\/+$/, '') : path;
          const to = routes.get(key)?.[lang];
          return to && to !== key ? `href="${to}${rest ?? ''}"` : match;
        }),
  );
}

export function sitemapXml(entries: readonly SitemapEntry[]): string {
  const urls = entries.map((e) => {
    const lines = [`    <loc>${escapeHtml(e.loc)}</loc>`];
    if (e.lastmod) lines.push(`    <lastmod>${e.lastmod}</lastmod>`);
    for (const [lang, href] of Object.entries(e.alternates ?? {})) {
      if (href) {
        lines.push(`    <xhtml:link rel="alternate" hreflang="${lang}" href="${escapeHtml(href)}" />`);
      }
    }
    return `  <url>\n${lines.join('\n')}\n  </url>`;
  });
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">',
    ...urls,
    '</urlset>',
    '',
  ].join('\n');
}

/**
 * /t/, /embed/ and /p/ are deliberately NOT disallowed: they send `noindex` (header + meta),
 * and a crawler can only obey that if it may fetch the page. A robots.txt block would also
 * stop the link-preview bots that honour robots.txt (Twitterbot, LinkedInBot,
 * facebookexternalhit) from reading a shared tour's OG tags.
 */
export function robotsTxt(origin: string = SITE.ORIGIN): string {
  return [
    'User-agent: *',
    'Allow: /',
    '# Signed-in dashboard and the raw viewer (also noindex).',
    'Disallow: /app$',
    'Disallow: /app/',
    'Disallow: /app?',
    'Disallow: /app.html',
    'Disallow: /tour$',
    'Disallow: /tour?',
    'Disallow: /tour.html',
    'Disallow: /tours/',
    '# View-count beacon.',
    'Disallow: /v/',
    'Disallow: /__/',
    '# /t/, /embed/ and /p/ stay crawlable on purpose: they answer noindex, and link-preview',
    '# bots must be able to read their Open Graph tags.',
    '',
    `Sitemap: ${origin}/sitemap.xml`,
    '',
  ].join('\n');
}
